import { comparePositions } from "@mapcraft-tasks/domain";
import type { Completion, Entities, EntityIds, Link, Node, Placement, Tag } from "./types";

/**
 * Живое дерево целиком в памяти. Нода ≈ 200–400 байт, 20 000 нод ≈ 5–8 МБ —
 * это обычные JS-мапы, а не повод тащить SQLite-wasm (offline-sync §2).
 */
export type Replica = {
	nodes: Map<string, Node>;
	placements: Map<string, Placement>;
	links: Map<string, Link>;
	completions: Map<string, Completion>;
	/** Оформление тегов по `tagId(name)`; тега без цвета здесь нет. */
	tags: Map<string, Tag>;
};

export function emptyReplica(): Replica {
	return { nodes: new Map(), placements: new Map(), links: new Map(), completions: new Map(), tags: new Map() };
}

/**
 * Копия для пересчёта `visible`.
 *
 * Спека предлагает точечный `overlay: Map<id, Node>`, и он дешевле по копированию —
 * но не выражает УДАЛЕНИЯ (`deleteNodes`, `removePlacement`) без второго набора-тумбстоунов
 * и третьего правила их слияния. На однопользовательском объёме копия четырёх мап
 * стоит около миллисекунды, а неверный откат удаления стоит потерянных данных.
 * Порог пересмотра — тот же, что у SQLite-wasm: ~100k нод.
 */
export function cloneReplica(source: Replica): Replica {
	return {
		nodes: new Map(source.nodes),
		placements: new Map(source.placements),
		links: new Map(source.links),
		completions: new Map(source.completions),
		tags: new Map(source.tags)
	};
}

export function applyEntities(replica: Replica, entities: Entities): void {
	for (const node of entities.nodes) {
		replica.nodes.set(node.id, node);
	}
	for (const placement of entities.placements) {
		replica.placements.set(placement.id, placement);
	}
	for (const link of entities.links) {
		replica.links.set(link.id, link);
	}
	for (const completion of entities.completions) {
		replica.completions.set(completion.id, completion);
	}
	for (const tag of entities.tags ?? []) {
		replica.tags.set(tag.id, tag);
	}
}

/**
 * `removed` — «выбыло из твоей реплики», а не «удалено навсегда». Нода может вернуться
 * через `restore` и приедет обычным `changed` вместе со своими рёбрами.
 *
 * Локального каскада у клиента НЕТ: он не додумывает удаление рёбер по удалению ноды,
 * а применяет только присланный набор. Одно место правды вместо двух расходящихся.
 */
export function removeEntities(replica: Replica, ids: EntityIds): void {
	for (const id of ids.nodes) {
		replica.nodes.delete(id);
	}
	for (const id of ids.placements) {
		replica.placements.delete(id);
	}
	for (const id of ids.links) {
		replica.links.delete(id);
	}
	for (const id of ids.completions) {
		replica.completions.delete(id);
	}
	for (const id of ids.tags ?? []) {
		replica.tags.delete(id);
	}
}

/** Строки контейнера: родные дети ∪ вплетения, единый keyspace, `ORDER BY position, id`. */
export function containerRows(
	replica: Replica,
	containerId: string
): { id: string; position: string; placementId: string | null }[] {
	const rows: { id: string; position: string; placementId: string | null }[] = [];
	for (const node of replica.nodes.values()) {
		if (node.parentId === containerId) {
			rows.push({ id: node.id, position: node.position, placementId: null });
		}
	}
	for (const placement of replica.placements.values()) {
		if (placement.containerId === containerId && replica.nodes.has(placement.nodeId)) {
			rows.push({ id: placement.nodeId, position: placement.position, placementId: placement.id });
		}
	}
	return rows.sort(comparePositions);
}

/** Поддеревья по `parent_id`, включая корни. Циклы placement сюда не попадают — обход только родной. */
export function subtreeIds(replica: Replica, rootIds: readonly string[]): string[] {
	const out = new Set<string>();
	const queue = [...rootIds];
	const childrenOf = new Map<string, string[]>();

	for (const node of replica.nodes.values()) {
		if (node.parentId === null) {
			continue;
		}
		const list = childrenOf.get(node.parentId);
		if (list) {
			list.push(node.id);
		} else {
			childrenOf.set(node.parentId, [node.id]);
		}
	}

	while (queue.length > 0) {
		const id = queue.pop() as string;
		if (out.has(id)) {
			continue;
		}
		out.add(id);
		for (const child of childrenOf.get(id) ?? []) {
			queue.push(child);
		}
	}
	return [...out];
}

/**
 * Слепок поддерева из реплики — локальная нагрузка отмены удаления (`CommandLocal`).
 *
 * Он заведомо беднее серверного: выполнения только за окно реплики, меншены не сверены
 * с текстом. Нужен ровно для одного — показать вернувшееся дерево, не дожидаясь ответа
 * сервера. Расхождение живёт до первого `pull` и схлопывается им; арбитр здесь сервер.
 *
 * Рёбра берутся по ОБЕИМ сторонам: удаление сносит и вплетения удаляемых нод, и вплетения
 * чужих нод в удаляемые контейнеры — редьюсер убирает и те, и другие.
 */
export function subtreeSnapshot(replica: Replica, nodeIds: readonly string[]): Entities {
	const doomed = new Set(nodeIds);
	return {
		nodes: [...doomed].map((id) => replica.nodes.get(id)).filter((node) => node !== undefined),
		placements: [...replica.placements.values()].filter((p) => doomed.has(p.nodeId) || doomed.has(p.containerId)),
		links: [...replica.links.values()].filter((l) => doomed.has(l.fromNodeId) || doomed.has(l.toNodeId)),
		completions: [...replica.completions.values()].filter((c) => doomed.has(c.nodeId))
	};
}

export function ancestorIds(replica: Replica, nodeId: string): string[] {
	const out: string[] = [];
	const seen = new Set<string>([nodeId]);
	let cursor = replica.nodes.get(nodeId)?.parentId ?? null;
	while (cursor && !seen.has(cursor)) {
		seen.add(cursor);
		out.push(cursor);
		cursor = replica.nodes.get(cursor)?.parentId ?? null;
	}
	return out;
}
